import type { UnitStats } from '@/modules/admin-v2/hooks/useDashboardData';
import type { AmenityReservation, Announcement } from '@yawi/types';
import { useNavigate } from 'react-router-dom';

interface CommunityCardProps {
  unitStats: UnitStats | null;
  upcomingReservations: AmenityReservation[];
  announcements: Announcement[];
  loadingUnits?: boolean;
  loadingCommunity?: boolean;
  showUnits?: boolean;
  showReservation?: boolean;
  showAnnouncement?: boolean;
}

const formatReservationDate = (date: string, startTime: string) => {
  try {
    const d = new Date(`${date}T${startTime}`);
    return d.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return `${date} ${startTime}`;
  }
};

const formatAnnouncementDate = (date: string) => {
  try {
    return new Date(date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  } catch {
    return date;
  }
};

export function CommunityCard({
  unitStats,
  upcomingReservations,
  announcements,
  loadingUnits,
  loadingCommunity,
  showUnits = true,
  showReservation = true,
  showAnnouncement = true,
}: CommunityCardProps) {
  const navigate = useNavigate();
  const reservation = upcomingReservations[0];
  const announcement = announcements[0];

  return (
    <div className="bg-white dark:bg-surface-secondary rounded-xl border-[0.5px] border-[#E8E6F8] dark:border-theme-light p-4 flex flex-col gap-3.5 flex-1">
      <div className="flex items-center justify-between">
        <h4 className="text-[15px] font-semibold text-[#1A1A18] dark:text-theme">Comunidad</h4>
        {showUnits && (
          <button
            onClick={() => navigate('/admin/unidades')}
            className="text-[12px] text-[#7F77DD] dark:text-primary-400 font-medium hover:underline"
          >
            Ver unidades
          </button>
        )}
      </div>

      <div className="flex flex-col divide-y divide-[#F0EFF9] dark:divide-theme-light">
        {/* Unidades */}
        {showUnits && (
          <div className="pb-3.5">
            {loadingUnits ? (
              <div className="space-y-1.5">
                <div className="h-3 w-20 bg-theme-light/40 rounded animate-pulse" />
                <div className="h-2 w-32 bg-theme-light/30 rounded animate-pulse" />
              </div>
            ) : unitStats ? (
              <>
                <p className="text-[13px] font-semibold text-[#1A1A18] dark:text-theme leading-tight">
                  {unitStats.total} unidades registradas
                </p>
                <p className="text-[11px] text-[#888780] dark:text-theme-tertiary leading-tight mt-0.5">
                  {unitStats.apartments} departamentos · {unitStats.houses} casas
                </p>
              </>
            ) : (
              <p className="text-[13px] text-[#888780] dark:text-theme-tertiary">Sin datos de unidades</p>
            )}
          </div>
        )}

        {/* Próxima reservación */}
        {showReservation && (
          <div className="py-3.5">
            <p className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#888780] dark:text-theme-tertiary">Próxima reserva</p>
            {loadingCommunity ? (
              <div className="space-y-1.5 mt-1">
                <div className="h-3 w-32 bg-theme-light/40 rounded animate-pulse" />
                <div className="h-2 w-40 bg-theme-light/30 rounded animate-pulse" />
              </div>
            ) : reservation ? (
              <>
                <p className="text-[13px] font-semibold text-[#1A1A18] dark:text-theme leading-tight truncate mt-0.5">
                  {reservation.amenity?.name ?? 'Amenidad'}
                </p>
                <p className="text-[11px] text-[#888780] dark:text-theme-tertiary leading-tight truncate mt-0.5">
                  {formatReservationDate(reservation.date, reservation.startTime)} · {reservation.memberName ?? reservation.member?.displayName ?? 'Residente'}
                </p>
              </>
            ) : (
              <p className="text-[13px] text-[#888780] dark:text-theme-tertiary mt-0.5">Sin reservas próximas</p>
            )}
          </div>
        )}

        {/* Último anuncio */}
        {showAnnouncement && (
          <div className="pt-3.5">
            <p className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#888780] dark:text-theme-tertiary">Último anuncio</p>
            {loadingCommunity ? (
              <div className="space-y-1.5 mt-1">
                <div className="h-3 w-36 bg-theme-light/40 rounded animate-pulse" />
                <div className="h-2 w-24 bg-theme-light/30 rounded animate-pulse" />
              </div>
            ) : announcement ? (
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <p className="text-[13px] font-semibold text-[#1A1A18] dark:text-theme leading-tight truncate">{announcement.title}</p>
                <span className="text-[11px] text-[#888780] dark:text-theme-tertiary flex-shrink-0">
                  {formatAnnouncementDate(announcement.date)}
                </span>
              </div>
            ) : (
              <p className="text-[13px] text-[#888780] dark:text-theme-tertiary mt-0.5">Sin anuncios recientes</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
